import React, { useState, useMemo } from "react";
import {
  Box,
  Modal,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  CloseOutlined,
  Search,
  ClearOutlined,
  ContentCopyOutlined,
  CheckOutlined,
  LanOutlined,
  SearchOffOutlined,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import useStyles from "./styles";

export default function PrefixesModal({
  open,
  onClose,
  interfaceName = "",
  ipSession = "",
  hostName = "",
  prefixes = [],
  loading = false,
}) {
  const theme = useTheme();
  const styles = useStyles?.(theme) || {};

  const [search, setSearch] = useState("");
  const [copiedPrefix, setCopiedPrefix] = useState(null);
  const [copiedSessionIp, setCopiedSessionIp] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  // Filtragem da lista apenas por prefixSession
  const filteredPrefixes = useMemo(() => {
    if (!prefixes || !Array.isArray(prefixes)) return [];
    if (!search?.trim?.()) return prefixes;

    const searchLower = search?.trim?.()?.toLowerCase?.();
    return (
      prefixes?.filter?.((item) => {
        const prefixStr = (
          typeof item === "string" ? item : item?.prefixSession || ""
        )?.toLowerCase?.();
        return prefixStr?.includes?.(searchLower);
      }) || []
    );
  }, [prefixes, search]);

  // Copiar prefixo individual
  const handleCopyPrefix = (prefixText) => {
    if (!prefixText) return;
    navigator?.clipboard?.writeText?.(prefixText);
    setCopiedPrefix(prefixText);
    setTimeout(() => setCopiedPrefix(null), 2000);
  };

  // Copiar IP / Nome da Sessão
  const handleCopySession = () => {
    const textToCopy = ipSession || interfaceName;
    if (!textToCopy) return;
    navigator?.clipboard?.writeText?.(textToCopy);
    setCopiedSessionIp(true);
    setTimeout(() => setCopiedSessionIp(false), 2000);
  };

  // Copiar todos os prefixos filtrados
  const handleCopyAll = () => {
    if (!filteredPrefixes?.length) return;
    const allText = filteredPrefixes
      ?.map?.((p) => (typeof p === "string" ? p : p?.prefixSession || ""))
      ?.filter?.(Boolean)
      ?.join?.("\n");

    if (allText) {
      navigator?.clipboard?.writeText?.(allText);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }
  };

  const handleClose = () => {
    setSearch("");
    setCopiedPrefix(null);
    setCopiedSessionIp(false);
    onClose?.();
  };

  return (
    <Modal open={Boolean(open)} onClose={handleClose} sx={styles?.modal}>
      <Paper elevation={1} sx={styles?.paper}>
        {/** Cabeçalho: Ícone, Título e Botão Fechar */}
        <Box sx={styles?.header}>
          <Box sx={styles?.headerTitleContainer}>
            <Box sx={styles?.headerIconBox}>
              <LanOutlined sx={styles?.headerIcon} />
            </Box>
            <Box sx={styles?.headerTitleBox}>
              <Typography variant="h6" sx={styles?.headerTitle}>
                Prefixos Recebidos
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Fechar"
            sx={styles?.closeButton}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>

        {/** Subheader: Host e Sessão (com tooltip e clique para copiar IP) */}
        <Box sx={styles?.subHeader}>
          {/** Host */}
          <Box sx={styles?.infoGroup}>
            <Typography variant="caption" sx={styles?.infoLabel}>
              Host:
            </Typography>
            <Typography variant="body2" sx={styles?.infoValueHost}>
              {hostName || "-"}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={styles?.divider} />

          {/** Sessão com Tooltip e Copiar */}
          <Box sx={styles?.infoGroup}>
            <Typography variant="caption" sx={styles?.infoLabel}>
              Sessão:
            </Typography>

            <Tooltip
              title={
                copiedSessionIp
                  ? "IP copiado!"
                  : "Clique para copiar IP da sessão"
              }
              placement="top"
              arrow
            >
              <Box
                component="span"
                onClick={handleCopySession}
                sx={styles?.sessionBadge}
              >
                <Typography variant="body2" sx={styles?.sessionBadgeText}>
                  {interfaceName || ipSession || "-"}
                </Typography>
                {copiedSessionIp ? (
                  <CheckOutlined sx={styles?.sessionIconSuccess} />
                ) : (
                  <ContentCopyOutlined sx={styles?.sessionIconCopy} />
                )}
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/** Campo de Buscar apenas por Prefixo */}
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por prefixo (ex: 192.168.0.0/24)..."
          value={search}
          disabled={loading}
          onChange={(e) => setSearch(e?.target?.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={styles?.searchAdornmentIcon} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearch("")}
                  edge="end"
                  aria-label="Limpar busca"
                >
                  <ClearOutlined fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={styles?.searchInput}
        />

        {/** Lista com estilo de linhas com cores alternadas */}
        <Box sx={styles?.listContainer}>
          {loading ? (
            <Box sx={styles?.emptyStateContainer}>
              <CircularProgress size={28} />
              <Typography variant="body2" sx={styles?.emptyStateText}>
                Buscando prefixos recebidos...
              </Typography>
            </Box>
          ) : filteredPrefixes?.length === 0 ? (
            <Box sx={styles?.emptyStateContainer}>
              <SearchOffOutlined sx={styles?.emptyStateIcon} />
              <Typography variant="body2" sx={styles?.emptyStateText}>
                {search
                  ? `Nenhum prefixo correspondente a "${search}"`
                  : "Nenhum prefixo recebido nesta sessão."}
              </Typography>
            </Box>
          ) : (
            filteredPrefixes?.map?.((item, index) => {
              const prefixStr =
                typeof item === "string" ? item : item?.prefixSession;
              const isCopied = copiedPrefix === prefixStr;

              return (
                <Tooltip
                  key={`${prefixStr}-${index}`}
                  title={isCopied ? "Prefixo copiado!" : "Clique para copiar"}
                  placement="top"
                  arrow
                >
                  <Box
                    onClick={() => handleCopyPrefix(prefixStr)}
                    sx={styles?.rowItem?.(index % 2 === 0)}
                  >
                    {/** Indicador bolinha + prefixSession */}
                    <Box sx={styles?.rowLeft}>
                      <Box sx={styles?.ballOuter}>
                        <Box sx={styles?.ballInner} />
                      </Box>

                      <Typography sx={styles?.prefixText}>
                        {prefixStr}
                      </Typography>
                    </Box>

                    {/** Ícone de cópia com feedback */}
                    <Box sx={styles?.rowRight?.(isCopied)}>
                      {isCopied ? (
                        <CheckOutlined sx={styles?.rowIconSuccess} />
                      ) : (
                        <ContentCopyOutlined sx={styles?.rowIconCopy} />
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })
          )}
        </Box>

        {/** Rodapé: Contagem e Ação rápida */}
        <Box sx={styles?.footer}>
          <Typography variant="caption" sx={styles?.footerText}>
            {loading
              ? "Carregando..."
              : filteredPrefixes?.length === (prefixes || [])?.length
                ? `Total: ${prefixes?.length || 0} prefixo${
                    prefixes?.length === 1 ? "" : "s"
                  }`
                : `Exibindo ${filteredPrefixes?.length || 0} de ${
                    prefixes?.length || 0
                  } prefixos`}
          </Typography>

          {!loading && (filteredPrefixes?.length || 0) > 0 && (
            <Tooltip
              title={copiedAll ? "Todos copiados!" : "Copiar lista visível"}
              placement="top"
              arrow
            >
              <Button
                size="small"
                variant="text"
                startIcon={
                  copiedAll ? (
                    <CheckOutlined sx={styles?.copyAllIconSuccess} />
                  ) : (
                    <ContentCopyOutlined sx={styles?.copyAllIconCopy} />
                  )
                }
                onClick={handleCopyAll}
                sx={styles?.copyAllButton?.(copiedAll)}
              >
                {copiedAll ? "Copiado!" : "Copiar todos"}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Paper>
    </Modal>
  );
}
